import { action, axiosReq } from './util';
import { validateEmail, validatePassword } from '../common/Validator';
import { LocalStorageKey } from '../common/Definitions';
import { setAuthToken, clearAuthToken } from '../common/Auth';
import config from '../config';

export const AuthActionType = {
  LOGIN: 'LOGIN',
  LOGGING_IN: 'LOGGING_IN',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  CHECK_AUTH: 'CHECK_AUTH',
  CHECKING_AUTH: 'CHECKING_AUTH',
  TOKEN_REFRESHING: 'TOKEN_REFRESHING',
  TOKEN_REFRESH_COMPLETE: 'TOKEN_REFRESH_COMPLETE',
};

export function checkAuth() {
  return async dispatch => {
    dispatch(checkingAuth());
    try {
      const { data: { message, user, version } } =
        await axiosReq().post('/api/user/checkauth');
      if (message === 'ok') {
        dispatch(loginSuccess(user));
      } else {
        dispatch(logout());
      }

      // Client Server version check
      if (config.version !== version) {
        const lastRefreshedVersion = localStorage.getItem(LocalStorageKey.LAST_REFRESHED_VERSION);
        // If haven't refreshed this version, reload
        if (lastRefreshedVersion !== version) {
          localStorage.setItem(LocalStorageKey.LAST_REFRESHED_VERSION, version);
          if (caches) {
            // Service worker cache should be cleared with caches.delete()
            caches.keys().then(function(names) {
              for (let name of names) caches.delete(name);
            });
            console.log('reset cache')
          }
  
          // delete browser cache and hard reload
          window.location.reload(true);
        }
      }
    } catch (error) {
      console.log(error);
      dispatch(logout());
    }
  };
}

export function checkingAuth() {
  return action(AuthActionType.CHECKING_AUTH);
}

export function login(email, password) {
  let validateError;
  if (!validateEmail(email)) {
    validateError = `Invalid Email Format: ${email}`;
  } else if (!validatePassword(password)) {
    validateError = 'Invalid Password Format. Must be 6-36 characters long.';
  }
  if (validateError) {
    return loginError(validateError);
  }

  return async dispatch => {
    dispatch(loginLoading());
    try {
      const { data: { message, token, refresh_token, user } } =
        await axiosReq().post('/api/user/login', {
          email,
          password,
        });
      if (message === 'ok' && token && user) {
        localStorage.setItem(LocalStorageKey.USER_ID, user.id);
        localStorage.setItem(LocalStorageKey.USER_SLUG, user.slug);
        localStorage.setItem(LocalStorageKey.REFRESH_TOKEN, refresh_token);
        // save auth_token in-memory
        setAuthToken(token);
        dispatch(loginSuccess(user));
      } else {
        dispatch(loginError('Unsuccessful Login'));
      }
    } catch (error) {
      console.log(error);
      const message = error.response && error.response.data ? error.response.data.error : 'Error occurred during Login';
      dispatch(loginError(message));
    }
  };
}

export function loginLoading() {
  return action(AuthActionType.LOGGING_IN);
}

export function loginSuccess(user) {
  return action(AuthActionType.LOGIN_SUCCESS, {
    user,
  });
}

export function loginError(error) {
  return action(AuthActionType.LOGIN_FAILURE, {
    error,
  });
}

export function logout(callback) {
  return async dispatch => {
    // invalidate refresh token
    const refresh_token = localStorage.getItem(LocalStorageKey.REFRESH_TOKEN);
    if (refresh_token) {
      try {
        await axiosReq().post('/api/user/logout', {
          refresh_token,
        });
      } catch (err) {
        // continue
      }
    }

    // Remove local storage keys
    localStorage.removeItem(LocalStorageKey.USER_ID);
    localStorage.removeItem(LocalStorageKey.USER_SLUG);
    localStorage.removeItem(LocalStorageKey.REFRESH_TOKEN);
    clearAuthToken();

    // Legacy storage key removal of auth_token
    localStorage.removeItem(LocalStorageKey.TOKEN);

    dispatch(action(AuthActionType.LOGOUT));

    // onLogout callback
    if (callback) {
      callback();
    }
  };
}

export function refreshingToken() {
  return action(AuthActionType.TOKEN_REFRESHING);
}

export function refreshTokenComplete() {
  return action(AuthActionType.TOKEN_REFRESH_COMPLETE);
}

export function refreshToken() {
  return async dispatch => {
    dispatch(refreshingToken());
    try {
      // Refresh auth_token using refresh_token jwt
      const refresh_token = localStorage.getItem(LocalStorageKey.REFRESH_TOKEN);
      if (refresh_token) {
        const { data: { message, token } } =
          await axiosReq().post('/api/user/refresh-token', {
            refresh_token,
          });

        // Save refreshed auth_token in-memory
        if (message === 'ok' && token) {
          setAuthToken(token);
        }
      }
      dispatch(refreshTokenComplete());
    } catch (error) {
      // Logout for unauthorized response; otherwise, continue polling
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      } else {
        dispatch(refreshTokenComplete());
      }
    }
  };
}
